import { Routes, Route, Navigate, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "./hooks/useAuth";
import { isAuthenticatedUser } from "./utils/authUser";
import { savePendingJoin, takePendingJoin } from "./utils/pendingJoin";
import { useTheme } from "./hooks/useTheme";
import LandingPage from "./pages/LandingPage";
import SystemLogs from "./pages/admin/SystemLogs";
import ContactMessages from "./pages/admin/ContactMessages";
import ProgressReport from "./pages/ProgressReport";
import Contact from "./pages/Contact";
import Support from "./pages/Support";
import GettingStarted from "./pages/resources/GettingStarted";
import TutorialsSupport from "./pages/resources/TutorialsSupport";
//old import Login from "./pages/auth/Login";
import Login, { pageBeforeSignIn } from "./pages/auth/Login";
import ActionItems from "./pages/ActionItems";
import ApplicationsTracker from "./pages/ApplicationsTracker";
import JobAdminPanel from "./pages/admin/JobManagement";    
import MyPreferences from "./pages/MyPreferences";
import MyMembership from "./pages/MyMembership";
import MembershipPlans from './pages/MembershipPlans';
import MembershipSuccess from './pages/MembershipSuccess';
import FreeTierSuccess from './pages/FreeTierSuccess';
import ScheduleTasksPage from "./pages/ScheduleTasks";
import BrandManager from "./pages/BrandManager";
import Signup from "./pages/auth/Signup";
import EmployerSignup from "./pages/auth/EmployerSignup";
import VerifyEmail from "./pages/auth/VerifyEmail";
import ForgotPassword from "./pages/auth/ForgotPassword";
import OnboardingGate, { shouldForceOnboarding } from "./pages/OnboardingGate";
import Sidebar from "./components/layout/Sidebar";
import Navbar from "./components/Navbar";
import UserDashboard from "./pages/Userdashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UserManagement from "./pages/admin/UserManagement";
import LookupManagement from "./pages/admin/LookupManagement";
import MembershipManagement from "./pages/admin/MembershipManagement";
import EmployeeManagement from "./pages/admin/EmployeeManagement";
import PermissionManagement from "./pages/admin/PermissionManagement";
import EmailTemplateManagement from "./pages/admin/EmailTemplateManagement";
import RequirePermission from "./components/RequirePermission";
import JobBrowser from "./pages/JobBrowser";
import MyCoaching from "./pages/MyCoaching";
import InviteCoaches from "./pages/coaching/InviteCoaches";
import CoachingDashboard from "./pages/coach/CoachingDashboard";
import MyStudents from "./pages/coach/MyStudents";
import MyProspects from "./pages/coach/MyProspects";
import CoachReporting from "./pages/coach/CoachReporting";
import CoachPreferences from "./pages/coach/CoachPreferences";
import CoachMembership from "./pages/coach/CoachMembership";
import CoachPlans from "./pages/coach/CoachPlans";
import CoachProfile from "./pages/coach/CoachProfile";
import EmployerDashboard from "./pages/employer/EmployerDashboard";
import EmployerApply from "./pages/employer/EmployerApply";
import AdminOnboardingChecklist from "./pages/admin/AdminOnboardingChecklist";
import SurveyManagement from "./pages/admin/SurveyManagement";
import ResumeScoringManagement from "./pages/admin/ResumeScoringManagement";
import CareerSuccessIndex from "./pages/CareerSuccessIndex";
import CSIResult from "./pages/CSIResult";
import PublicSurveyPage from "./pages/PublicSurveyPage";
import BookingRequest from "./pages/BookingRequest";
import ResumeAnalysis from "./pages/ResumeAnalysis";
import Notifications from "./pages/Notifications";
import Footer from "./components/Footer";
import BottomNav from "./components/layout/BottomNav";
import MockInterview from "./pages/MockInterview";
import InterviewManagement from "./pages/admin/InterviewManagement";
import PitchManagement from "./pages/admin/PitchManagement";
import ResumeAnalysisComingSoon from "./pages/ResumeAnalysisComingSoon";
import InterviewSession from "./pages/InterviewSession";
import PitchSession from "./pages/PitchSession";
import ComingSoon from "./components/ComingSoon";
import OrgSignup from "./pages/auth/OrgSignup";
import CommunityPartnerSignup from "./pages/auth/CommunityPartnerSignup";
import PublicJobs from "./pages/PublicJobs";
import OrgLayout from "./pages/org/OrgLayout";
import OrgOverview from "./pages/org/OrgOverview";
import OrgPrograms from "./pages/org/OrgPrograms";
import OrgStudents from "./pages/org/OrgStudents";
import OrgLicenses from "./pages/org/OrgLicenses";
import OrgStaff from "./pages/org/OrgStaff";
import OrgSettings from "./pages/org/OrgSettings";
import OrgScoring from "./pages/org/OrgScoring";
import OrgRequests from "./pages/org/OrgRequests";
import OrgImpact from "./pages/org/OrgImpact";
import JoinProgram from "./pages/auth/JoinProgram";
import OrganizationManagement from "./pages/admin/OrganizationManagement";

// Comma-separated feature keys (set via REACT_APP_COMING_SOON_FEATURES in the
// build pipeline, production only) to show a "Coming Soon" page for instead
// of the real route. Unset on dev, so dev always renders the real page.
const COMING_SOON_FEATURES = (process.env.REACT_APP_COMING_SOON_FEATURES || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);
const isComingSoon = (key) => COMING_SOON_FEATURES.includes(key);

// An organization admin login (not a coach): manages an org, has no client profile.
const isOrgAdminOnly = (user) => !!(user && user.isOrgAdmin && !user.isCoach && !user.isAdmin);

// The pre-plan signup gates (verify email / profile / pick a membership) — the
// states that trap a user before they've actually finished signing up.
function needsCoreSignup(user) {
  if (!user || user.isAdmin || user.isCoach || user.isEmployer || user.isOrgAdmin) return false;
  return (
    user.emailVerified === false ||
    user.profileComplete === false ||
    user.onboardingComplete === false
  );
}

// A user who still hasn't finished the core signup (above) and opens the homepage
// URL is signalling "I want out" — so instead of trapping them in the forced
// onboarding flow, sign them out and drop them on the public homepage. When they
// log back in, the normal onboarding resume takes them right back to where they
// left off (their saved signup stage), so nothing is lost.
function HomeEscape({ logout }) {
  useEffect(() => {
    logout("/");
  }, [logout]);
  return <LandingPage />;
}

export default function App() {
  const { user, loading, logout, checkAuth } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLinkedInAuth, setIsLinkedInAuth] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // /signup is a registration page and must never show the app chrome (sidebar/
  // navbar), even for an authed user still mid-signup (e.g. back from stage 3 to
  // the profile step). Users who've actually finished are redirected away by the
  // /signup route guard, so they never render here.
  //old const hideAppChrome = location.pathname === '/signup' || location.pathname === '/organizations/signup' || location.pathname === '/verify-email' || location.pathname === '/forgot-password' || location.pathname.startsWith('/survey/') || location.pathname.startsWith('/join/') || location.pathname === '/interviewSession' || location.pathname === '/pitchSession';
  const hideAppChrome = location.pathname === '/signup' || location.pathname === '/organizations/signup' || location.pathname === '/community-partners/signup' || location.pathname === '/jobs' || location.pathname === '/verify-email' || location.pathname === '/forgot-password' || location.pathname.startsWith('/survey/') || location.pathname.startsWith('/join/') || location.pathname === '/interviewSession' || location.pathname === '/pitchSession';

  // Do not treat a truthy but empty/invalid object as logged in (ghost / undefined name).
  const isAuthed = isAuthenticatedUser(user);
  // Regular (job-seeking) users get the mobile bottom navigation bar.
  const isRegularUser = isAuthed && user && !user.isAdmin && !user.isCoach && !user.isEmployer && !user.isOrgAdmin;

  const signupStage2FromUrl =
    location.pathname === '/signup' &&
    new URLSearchParams(location.search).get('stage') === '2';
  // An authed user should only be shown the Signup page when the URL carries a real
  // resume context — the email-flow ?stage=2, or ?fromDashboard=1/2 from the
  // onboarding gate. Bare /signup for an authed user isn't a real signup entry, so
  // we send them home instead of rendering the "Create Account" step 1.
  const signupHasResumeParam =
    signupStage2FromUrl ||
    (location.pathname === '/signup' &&
      ['1', '2'].includes(new URLSearchParams(location.search).get('fromDashboard')));
  /**
   * Logged-in client who still needs to be *rendered* the Signup page rather than
   * bounced to the dashboard: the profile step isn't done, no plan is picked yet,
   * or a stale user object on refresh at ?stage=2. This must stay in sync with
   * OnboardingGate's gates (profileComplete / onboardingComplete) — both redirect
   * here, so if this guard doesn't recognize the same states it ping-pongs
   * /signup -> /userdashboard -> /onboarding -> /signup (a navigation loop).
   */
  const clientMayResumeSignup =
    isAuthed &&
    user &&
    !user.isCoach &&
    !user.isAdmin &&
    !user.isEmployer &&
    !user.isOrgAdmin &&
    (user.profileComplete === false ||
      user.onboardingComplete === false ||
      (signupStage2FromUrl && user.onboardingComplete !== true));

  // Handle LinkedIn OAuth redirect - refresh auth state when linkedin=success
  useEffect(() => {
    if (searchParams.get('linkedin') === 'success') {
      // Show loading screen
      setIsLinkedInAuth(true);
      
      // Remove the query parameter from URL
      searchParams.delete('linkedin');
      setSearchParams(searchParams, { replace: true });
      
      // Wait for session cookie to be available before checking auth
      const waitForCookie = async () => {
      // Wait a bit for the cookie to be processed by the browser
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Retry checkAuth a few times if it fails (cookie might not be ready yet)
      let attempts = 0;
      const maxAttempts = 5;
        
      while (attempts < maxAttempts) {
          try {
            await checkAuth();
            // If checkAuth succeeds, break out of the loop
            break;
          } catch (error) {
            attempts++;
            if (attempts < maxAttempts) {
              // Wait a bit longer before retrying
              await new Promise(resolve => setTimeout(resolve, 200));
            } else {
              console.error("Failed to verify session after LinkedIn login");
            }
          }
        }
      };
      
      waitForCookie().finally(() => {
        // Hide loading screen after auth check completes
        setIsLinkedInAuth(false);
      });
    }
  }, [searchParams, checkAuth, setSearchParams]);

  // Reset scroll position on every navigation instead of preserving the previous page's scroll offset.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Check if we're on public pages (landing/login/signup) - these should always be light mode
  //old const isPublicPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/organizations/signup' || location.pathname === '/employer/signup' || location.pathname === '/verify-email' || location.pathname === '/forgot-password' || location.pathname.startsWith('/survey/') || location.pathname.startsWith('/join/');
  const isPublicPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/organizations/signup' || location.pathname === '/community-partners/signup' || location.pathname === '/jobs' || location.pathname === '/employer/signup' || location.pathname === '/verify-email' || location.pathname === '/forgot-password' || location.pathname.startsWith('/survey/') || location.pathname.startsWith('/join/');
  //old const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/organizations/signup' || location.pathname === '/employer/signup' || location.pathname === '/verify-email' || location.pathname === '/forgot-password';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/organizations/signup' || location.pathname === '/community-partners/signup' || location.pathname === '/employer/signup' || location.pathname === '/verify-email' || location.pathname === '/forgot-password';

    // Helper: determine where a freshly-logged-in user should land
    const defaultRedirect = isAuthed && user
        ? user.isCoach
            ? "/coaching/dashboard"
            : isOrgAdminOnly(user)
            ? "/org"
            : user.isAdmin
                ? "/admin/dashboard"
                : user.isEmployer
                    ? "/employer/dashboard"
                    : "/userdashboard"
        : null;

  const forceOnboarding = shouldForceOnboarding(user) && location.pathname !== "/onboarding";

  // Program invitation links (/join/<code>): remember the code, so a student who
  // has to log in or finish signup first is brought back to it afterwards.
  useEffect(() => {
    if (!location.pathname.startsWith("/join/") || (isAuthed && !isRegularUser)) return;
    //old code
    //const code = decodeURIComponent(location.pathname.slice("/join/".length)).trim();
    // A mangled link (/join/50%) must not crash the app into a blank page;
    // JoinProgram shows its "link doesn't work" message instead
    let code = "";
    try {
      code = decodeURIComponent(location.pathname.slice("/join/".length)).trim();
    } catch {
      code = "";
    }
    if (code) savePendingJoin(code);
  }, [location.pathname, isAuthed, isRegularUser]);

  // ...and once they reach their dashboard, take them to join it (once).
  useEffect(() => {
    if (!isRegularUser || forceOnboarding || location.pathname !== "/userdashboard") return;
    const code = takePendingJoin();
    if (code) navigate(`/coaching?tab=programs&code=${encodeURIComponent(code)}`);
  }, [isRegularUser, forceOnboarding, location.pathname, navigate]);

  // Show LinkedIn auth loading screen
  if (isLinkedInAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Completing Sign In</h2>
          <p className="text-slate-600">Please wait while we set up your account...</p>
        </div>
      </div>
    );
  }

  // Don't block first paint for public pages.
  if (loading && !isPublicPage) {
    return (
      <div className={`flex items-center justify-center min-h-screen transition-colors ${
        isDarkMode ? 'bg-slate-900' : 'bg-slate-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Pono Pilot...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex min-h-screen w-full h-full transition-colors ${
        isPublicPage ? 'bg-white' : (isDarkMode ? 'bg-surface' : 'bg-surface')
    }`}>
      {/* Persist UI elements - only when we have a real session (not a ghost object) */}
      {isAuthed && !hideAppChrome && (
        <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
      )}

      <div className="flex-1 flex flex-col min-w-0 w-full h-full">
        {isAuthed && !hideAppChrome && (
          <Navbar
            user={user}
            onLogout={logout}
            mobileNavOpen={mobileNavOpen}
            onMenuClick={() => setMobileNavOpen((open) => !open)}
          />
        )}

        <main className={`flex-1 overflow-x-hidden w-full h-full transition-colors ${
            isRegularUser && !isPublicPage ? 'pb-28 md:pb-0' : 'pb-0'
        } ${
            isPublicPage ? 'bg-white p-0' : (isDarkMode ? 'bg-surface p-6' : 'bg-surface p-6')
        }`}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={isAuthed && user ? (needsCoreSignup(user) ? <HomeEscape logout={logout} /> : user.isCoach ? <Navigate to="/coaching/dashboard" replace /> : isOrgAdminOnly(user) ? <Navigate to="/org" replace /> : user.isAdmin ? <Navigate to="/admin/dashboard" replace /> : <Navigate to="/userdashboard" replace />) : <LandingPage />} />
            <Route
              path="/login"
              element={
                !isAuthed ? (
                  <Login isDarkMode={false} />
                ) : pageBeforeSignIn(location) && !shouldForceOnboarding(user) ? (
                  // back to the page that sent them to sign in, as Login does (a student finishes signing up first)
                  <Navigate to={pageBeforeSignIn(location)} replace />
                ) : user.isCoach ? (
                  <Navigate to="/coaching/dashboard" replace />
                ) : isOrgAdminOnly(user) ? (
                  <Navigate to="/org" replace />
                ) : user.isAdmin ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : user.isEmployer ? (
                  <Navigate to="/employer/dashboard" replace />
                ) : clientMayResumeSignup ? (
                  <Navigate to="/signup?stage=2" replace />
                ) : (
                  <Navigate to="/userdashboard" replace />
                )
              }
            />
            <Route
              path="/signup"
              element={
                !isAuthed ? (
                  <Signup />
                ) : user.isCoach ? (
                  <Navigate to="/coaching/dashboard" replace />
                ) : isOrgAdminOnly(user) ? (
                  <Navigate to="/org" replace />
                ) : user.isAdmin ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : user.isEmployer ? (
                  <Navigate to="/employer/dashboard" replace />
                ) : clientMayResumeSignup && signupHasResumeParam ? (
                  <Signup />
                ) : clientMayResumeSignup ? (
                  // Refreshed mid-signup (bare /signup, or ?code / ?invite): carry on in the
                  // resume mode instead of going to '/', which signs them out (HomeEscape)
                  <Navigate to={`/signup?${new URLSearchParams({ ...Object.fromEntries(searchParams), fromDashboard: '1' })}`} replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/employer/signup" element={isAuthed ? <Navigate to={defaultRedirect} replace /> : <EmployerSignup />} />
            {/* Organization signup lives on its own route, separate from personal /signup and the /org portal. */}
            <Route path="/organizations/signup" element={isAuthed ? <Navigate to={defaultRedirect} replace /> : <OrgSignup />} />
            {/* Community Partners (nonprofits, Oct 16): their own sign-up page */}
            <Route path="/community-partners/signup" element={isAuthed ? <Navigate to={defaultRedirect} replace /> : <CommunityPartnerSignup />} />
            {/* Job preview for the official website's Job Search page (no sign-in needed) */}
            <Route path="/jobs" element={<PublicJobs />} />
            <Route path="/org/signup" element={<Navigate to="/organizations/signup" replace />} />
            {/* Program invitation link: students land on Programs, visitors sign up with the code. */}
            <Route path="/join/:code" element={<JoinProgram isAuthed={isAuthed} isStudent={isRegularUser} />} />
            {/* Reachable by authed-but-unverified users (OnboardingGate gate 1) and during signup. */}
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/book-session" element={<BookingRequest />} />
            <Route path="/survey/:id" element={<PublicSurveyPage />} />

            {/* Protected Routes */}
            {!isAuthed ? (
              //old <Route path="*" element={<Navigate to="/login" replace />} />
              // the page they came for (e.g. an emailed Licenses link) goes along, so signing in comes back to it
              <Route path="*" element={<Navigate to="/login" replace state={{ from: location }} />} />
            ) : (
              <>
                {forceOnboarding ? (
                  <>
                    <Route path="/onboarding" element={<OnboardingGate />} />
                    {/* Back from paying for a plan at signup: the plan isn't recorded as
                        picked yet, so these have to render here too, or verify-session
                        never runs and the user is sent back to the plan picker. */}
                    <Route path="/membership/success" element={<MembershipSuccess />} />
                    <Route path="/membership/free-success" element={<FreeTierSuccess />} />
                    <Route path="*" element={<Navigate to="/onboarding" replace />} />
                  </>
                ) : (
                  <>
                {/* Admin Routes */}
                {user.isAdmin && (
                  <>
                    <Route path="/admin/dashboard" element={<RequirePermission><AdminDashboard /></RequirePermission>} />
                    <Route path="/admin/users" element={<RequirePermission><UserManagement /></RequirePermission>} />
                    <Route path="/admin/lookups" element={<RequirePermission><LookupManagement /></RequirePermission>} />
                    <Route path="/admin/memberships" element={<RequirePermission><MembershipManagement /></RequirePermission>} />
                    <Route path="/admin/employees" element={<RequirePermission><EmployeeManagement /></RequirePermission>} />
                    <Route path="/admin/permissions" element={<RequirePermission><PermissionManagement /></RequirePermission>} />
                    <Route path="/admin/jobmanagement" element={<RequirePermission><JobAdminPanel /></RequirePermission>} />
                    <Route path="/admin/onboarding-checklist" element={<RequirePermission><AdminOnboardingChecklist /></RequirePermission>} />
                    <Route path="/admin/surveys" element={<RequirePermission><SurveyManagement /></RequirePermission>} />
                    <Route path="/admin/resume-scoring" element={<RequirePermission><ResumeScoringManagement /></RequirePermission>} />
                    <Route path="/admin/interviewmanagement" element={<RequirePermission><InterviewManagement /></RequirePermission>} />
                    <Route path="/admin/pitchmanagement" element={<RequirePermission><PitchManagement /></RequirePermission>} />
                    <Route path="/admin/system-logs" element={<RequirePermission><SystemLogs /></RequirePermission>} />
                    <Route path="/admin/contact-messages" element={<RequirePermission><ContactMessages /></RequirePermission>} />
                    <Route path="/admin/organizations" element={<RequirePermission><OrganizationManagement /></RequirePermission>} />
                    <Route path="/admin/email-templates" element={<RequirePermission><EmailTemplateManagement /></RequirePermission>} />
                  </>
                )}
                {/* Organization portal: org admins, org coaches, and Pono Pilot admins (?orgId=). Each section is its own page; OrgLayout loads the org, guards admin-only pages, and explains when a coach has no access. */}
                {(user.isOrgAdmin || user.isCoach || user.isAdmin) && (
                  <Route path="/org" element={<OrgLayout />}>
                    <Route index element={<OrgOverview />} />
                    <Route path="programs" element={<OrgPrograms />} />
                    <Route path="students" element={<OrgStudents />} />
                    <Route path="licenses" element={<OrgLicenses />} />
                    <Route path="scoring" element={<OrgScoring />} />
                    <Route path="requests" element={<OrgRequests />} />
                    <Route path="impact" element={<OrgImpact />} />
                    <Route path="staff" element={<OrgStaff />} />
                    <Route path="settings" element={<OrgSettings />} />
                    <Route path="*" element={<OrgOverview />} />
                  </Route>
                )}
                {/* Employer Routes */}
                {(user.isEmployer || user.isAdmin) && (
                  <Route path="/employer/dashboard" element={<EmployerDashboard />} />
                )}
                <Route path="/employer/apply" element={<EmployerApply />} />
                {/* Regular User Routes */}
                <Route path="/userdashboard" element={<UserDashboard />} />
                <Route path="/resume-analysis" element={isComingSoon('resumeAnalysis') ? <ResumeAnalysisComingSoon /> : <ResumeAnalysis />} />
                <Route path="/brandManager" element={<BrandManager />} />
                <Route path="/actionitems" element={<ActionItems />} />
                <Route path="/scheduletasks" element={<ScheduleTasksPage />} />
                <Route path="/progressreport" element={<ProgressReport />} />
                <Route path="/career-success-index/user" element={<CareerSuccessIndex />} />
                <Route path="/career-success-index/user/result" element={<CSIResult />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/support" element={<Support />} />
                <Route path="/resources/getting-started" element={<GettingStarted />} />
                <Route path="/resources/tutorials-support" element={<TutorialsSupport />} />
                <Route path="/jobListings" element={<Navigate to="/jobbrowser" replace />} />
                <Route path="/applicationsTracker" element={<ApplicationsTracker />} />
                <Route path="/mockInterview" element={isComingSoon('mockInterview') ? <ComingSoon title="Mock Interview" isDarkMode={isDarkMode} /> : <MockInterview />} />
                <Route path="/interviewSession" element={<InterviewSession />} />
                <Route path="/pitchSession" element={<PitchSession />} />
                <Route path="/jobbrowser" element={<JobBrowser />} />
                <Route path="/preferences" element={<MyPreferences />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/membership" element={<MyMembership />} />
                <Route path="/membership/plans" element={<MembershipPlans />} />
                <Route path="/membership/success" element={<MembershipSuccess />} />
                <Route path="/membership/free-success" element={<FreeTierSuccess />} />
                <Route path="/coaching" element={<MyCoaching />} />
                <Route path="/coaching/find" element={<Navigate to="/coaching?tab=find" replace />} />
                <Route path="/coaching/suggestions" element={<Navigate to="/coaching?tab=suggestions" replace />} />
                <Route path="/coaching/feedback" element={<Navigate to="/coaching?tab=feedback" replace />} />
                <Route path="/coaching/invite" element={<InviteCoaches />} />
                {/* Coach Routes */}
                {(user.isCoach || user.isAdmin) && (
                  <>
                    <Route path="/coaching/dashboard" element={<CoachingDashboard />} />
                    <Route path="/coaching/students" element={<MyStudents />} />
                    <Route path="/coaching/prospects" element={<MyProspects />} />
                    <Route path="/coaching/reporting" element={<CoachReporting />} />
                    <Route path="/coaching/preferences" element={<CoachPreferences />} />
                    <Route path="/coaching/membership" element={<CoachMembership />} />
                    <Route path="/coaching/membership/plans" element={<CoachPlans />} />
                    <Route path="/coaching/profile" element={<CoachProfile />} />

                  </>
                )}
                <Route path="/onboarding" element={<OnboardingGate />} />
                  </>
                )}
              </>
            )}
          </Routes>
        </main>
        {!isAuthPage && <Footer />}
      </div>
      {isRegularUser && !hideAppChrome && <BottomNav />}
    </div>
  );
}
